/**
 * Store de chat con Zustand
 * Sprint 11 - HU-FE-01/02/03/04/05
 */

import { create } from 'zustand';
import { chatService } from '../services/chat';
import { useAuthStore } from './authStore';
import type {
    ChatApiError,
    ChatConversation,
    ChatHistoryMode,
    ChatMessage,
    ChatMessageEventPayload,
    ChatPaginationMeta,
    MarkConversationAsReadResponse,
} from '../types/chat';

interface ConversationMessagesState {
    items: ChatMessage[];
    meta: (ChatPaginationMeta & { sort: 'desc' | 'asc' }) | null;
}

interface FetchMessagesOptions {
    page?: number;
    per_page?: number;
    append?: boolean;
    silent?: boolean;
}

interface MarkConversationAsReadOptions {
    lastReadMessageId?: number;
    silent?: boolean;
}

type ConversationReadState = MarkConversationAsReadResponse['data'];

interface ChatState {
    conversations: ChatConversation[];
    conversationsMeta: ChatPaginationMeta | null;
    unreadTotal: number;
    selectedConversation: ChatConversation | null;
    messagesByConversation: Record<number, ConversationMessagesState>;
    activeConversationId: number | null;
    isLoadingConversations: boolean;
    isLoadingConversationDetail: boolean;
    isLoadingMessages: boolean;
    isSendingMessage: boolean;
    isCreatingConversation: boolean;
    isAddingParticipants: boolean;
    error: string | null;
    fetchConversations: (perPage?: number) => Promise<void>;
    fetchConversation: (conversationId: number) => Promise<void>;
    fetchMessages: (conversationId: number, options?: FetchMessagesOptions) => Promise<void>;
    sendMessage: (conversationId: number, body: string) => Promise<ChatMessage | null>;
    addParticipantsToConversation: (
        conversationId: number,
        participantIds: number[],
        historyMode: ChatHistoryMode
    ) => Promise<ChatConversation | null>;
    markConversationAsRead: (
        conversationId: number,
        options?: MarkConversationAsReadOptions
    ) => Promise<ConversationReadState | null>;
    updateConversationPresence: (conversationId: number, active: boolean) => Promise<void>;
    createConversation: (participantIds: number[]) => Promise<ChatConversation | null>;
    findOrCreateAdminDirectConversation: (targetUserId: number) => Promise<ChatConversation | null>;
    setActiveConversationId: (conversationId: number | null) => void;
    appendRealtimeMessage: (eventPayload: ChatMessageEventPayload) => void;
    incrementUnreadFromPush: (conversationId: number) => void;
    clearError: () => void;
    clearSelectedConversation: () => void;
}

function toErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.message.trim() !== '') {
        return error.message;
    }

    const apiError = error as ChatApiError | undefined;

    if (apiError?.message && apiError.message.trim() !== '') {
        return apiError.message;
    }

    return fallback;
}

function upsertMessageDescending(messages: ChatMessage[], message: ChatMessage): ChatMessage[] {
    const foundIndex = messages.findIndex((current) => current.id === message.id);

    if (foundIndex >= 0) {
        const next = [...messages];
        next[foundIndex] = message;
        return next;
    }

    return [message, ...messages];
}

function findConversationByTargetUser(
    conversations: ChatConversation[],
    targetUserId: number
): ChatConversation | null {
    const found = conversations.find((conversation) => {
        if (conversation.scope !== 'admin_direct' || conversation.status !== 'active') {
            return false;
        }

        const participants = conversation.participants ?? [];

        return participants.some((participant) => participant.user_id === targetUserId);
    });

    return found ?? null;
}

function upsertConversationById(
    conversations: ChatConversation[],
    conversation: ChatConversation
): ChatConversation[] {
    const alreadyExists = conversations.some((current) => current.id === conversation.id);

    if (!alreadyExists) {
        return [conversation, ...conversations];
    }

    return conversations.map((current) => (current.id === conversation.id ? conversation : current));
}

function normalizeUnreadCount(unreadCount: number | null | undefined): number {
    if (typeof unreadCount !== 'number' || Number.isNaN(unreadCount)) {
        return 0;
    }

    return unreadCount > 0 ? Math.floor(unreadCount) : 0;
}

function normalizeConversation(conversation: ChatConversation): ChatConversation {
    return {
        ...conversation,
        unread_messages_count: normalizeUnreadCount(conversation.unread_messages_count),
    };
}

export const useChatStore = create<ChatState>((set, get) => ({
    conversations: [],
    conversationsMeta: null,
    unreadTotal: 0,
    selectedConversation: null,
    messagesByConversation: {},
    activeConversationId: null,
    isLoadingConversations: false,
    isLoadingConversationDetail: false,
    isLoadingMessages: false,
    isSendingMessage: false,
    isCreatingConversation: false,
    isAddingParticipants: false,
    error: null,

    fetchConversations: async (perPage = 20) => {
        set({ isLoadingConversations: true, error: null });

        try {
            const response = await chatService.getConversations({ per_page: perPage });
            const normalizedConversations = response.data.map(normalizeConversation);
            const fallbackUnreadTotal = normalizedConversations.reduce((total, conversation) => {
                return total + normalizeUnreadCount(conversation.unread_messages_count);
            }, 0);
            set({
                conversations: normalizedConversations,
                conversationsMeta: response.meta,
                unreadTotal: typeof response.meta?.unread_total === 'number'
                    ? Math.max(0, Math.floor(response.meta.unread_total))
                    : fallbackUnreadTotal,
                isLoadingConversations: false,
            });
        } catch (error) {
            set({
                isLoadingConversations: false,
                error: toErrorMessage(error, 'No se pudo cargar el listado de conversaciones.'),
            });
        }
    },

    fetchConversation: async (conversationId) => {
        set({ isLoadingConversationDetail: true, error: null });

        try {
            const response = await chatService.getConversation(conversationId);
            const normalizedConversation = normalizeConversation(response.data);

            set((state) => {
                return {
                    selectedConversation: normalizedConversation,
                    conversations: upsertConversationById(state.conversations, normalizedConversation),
                    isLoadingConversationDetail: false,
                };
            });
        } catch (error) {
            set({
                isLoadingConversationDetail: false,
                error: toErrorMessage(error, 'No se pudo cargar la conversación.'),
            });
        }
    },

    fetchMessages: async (conversationId, options = {}) => {
        const page = options.page ?? 1;
        const perPage = options.per_page ?? 30;
        const append = options.append ?? false;
        const silent = options.silent ?? false;

        if (!silent) {
            set({ isLoadingMessages: true, error: null });
        }

        try {
            const response = await chatService.getConversationMessages(conversationId, {
                page,
                per_page: perPage,
            });

            set((state) => {
                const previous = state.messagesByConversation[conversationId];
                const previousItems = previous?.items ?? [];
                const nextItems = append
                    ? [...previousItems, ...response.data.filter((message) =>
                        !previousItems.some((existing) => existing.id === message.id)
                    )]
                    : response.data;
                const latestMessage = nextItems[0] ?? null;
                const currentConversation = state.conversations.find((conversation) => conversation.id === conversationId);
                const updatedConversation = currentConversation && latestMessage
                    ? {
                        ...currentConversation,
                        last_message: latestMessage,
                        last_message_at: latestMessage.created_at,
                        unread_messages_count: currentConversation.unread_messages_count,
                    }
                    : currentConversation;

                return {
                    messagesByConversation: {
                        ...state.messagesByConversation,
                        [conversationId]: {
                            items: nextItems,
                            meta: response.meta,
                        },
                    },
                    isLoadingMessages: silent ? state.isLoadingMessages : false,
                    selectedConversation: state.selectedConversation?.id === conversationId && latestMessage
                        ? {
                            ...state.selectedConversation,
                            last_message: latestMessage,
                            last_message_at: latestMessage.created_at,
                            unread_messages_count: state.selectedConversation.unread_messages_count,
                        }
                        : state.selectedConversation,
                    conversations: updatedConversation
                        ? upsertConversationById(state.conversations, updatedConversation)
                        : state.conversations,
                };
            });
        } catch (error) {
            if (!silent) {
                set({
                    isLoadingMessages: false,
                    error: toErrorMessage(error, 'No se pudo cargar el historial de mensajes.'),
                });
            }
        }
    },

    sendMessage: async (conversationId, body) => {
        set({ isSendingMessage: true, error: null });

        try {
            const response = await chatService.sendConversationMessage(conversationId, {
                body,
                type: 'text',
            });

            set((state) => {
                const previousMessages = state.messagesByConversation[conversationId] ?? {
                    items: [],
                    meta: null,
                };

                const nextMessages = upsertMessageDescending(previousMessages.items, response.data);

                const currentConversation = state.conversations.find((conversation) => conversation.id === conversationId);
                const previousUnreadCount = currentConversation
                    ? normalizeUnreadCount(currentConversation.unread_messages_count)
                    : 0;
                const updatedConversation = currentConversation
                    ? {
                        ...currentConversation,
                        last_message: response.data,
                        last_message_at: response.data.created_at,
                        unread_messages_count: 0,
                    }
                    : null;

                return {
                    isSendingMessage: false,
                    messagesByConversation: {
                        ...state.messagesByConversation,
                        [conversationId]: {
                            ...previousMessages,
                            items: nextMessages,
                        },
                    },
                    selectedConversation: state.selectedConversation?.id === conversationId
                        ? {
                            ...state.selectedConversation,
                            last_message: response.data,
                            last_message_at: response.data.created_at,
                            unread_messages_count: 0,
                        }
                        : state.selectedConversation,
                    conversations: updatedConversation
                        ? upsertConversationById(state.conversations, updatedConversation)
                        : state.conversations,
                    unreadTotal: Math.max(0, state.unreadTotal - previousUnreadCount),
                };
            });

            return response.data;
        } catch (error) {
            set({
                isSendingMessage: false,
                error: toErrorMessage(error, 'No se pudo enviar el mensaje.'),
            });

            return null;
        }
    },

    addParticipantsToConversation: async (conversationId, participantIds, historyMode) => {
        set({ isAddingParticipants: true, error: null });

        try {
            const response = await chatService.addConversationParticipants(conversationId, {
                participant_ids: participantIds,
                history_mode: historyMode,
            });
            const normalizedConversation = normalizeConversation(response.data);

            set((state) => ({
                isAddingParticipants: false,
                selectedConversation: state.selectedConversation?.id === conversationId
                    ? normalizedConversation
                    : state.selectedConversation,
                conversations: upsertConversationById(state.conversations, normalizedConversation),
            }));

            return normalizedConversation;
        } catch (error) {
            set({
                isAddingParticipants: false,
                error: toErrorMessage(error, 'No se pudieron agregar participantes a la conversación.'),
            });

            return null;
        }
    },

    updateConversationPresence: async (conversationId, active) => {
        try {
            await chatService.updateConversationPresence(conversationId, { active });
        } catch {
            // Best effort: errores de presencia no deben interrumpir UX.
        }
    },

    markConversationAsRead: async (conversationId, options = {}) => {
        const lastReadMessageId = options.lastReadMessageId;
        const silent = options.silent ?? false;

        try {
            const response = await chatService.markConversationAsRead(
                conversationId,
                lastReadMessageId !== undefined
                    ? { last_read_message_id: lastReadMessageId }
                    : {}
            );
            const readState = response.data;
            const nextUnreadCount = normalizeUnreadCount(readState.unread_messages_count);

            set((state) => ({
                selectedConversation: state.selectedConversation?.id === conversationId
                    ? {
                        ...state.selectedConversation,
                        unread_messages_count: nextUnreadCount,
                    }
                    : state.selectedConversation,
                conversations: state.conversations.map((conversation) => (
                    conversation.id === conversationId
                        ? {
                            ...conversation,
                            unread_messages_count: nextUnreadCount,
                        }
                        : conversation
                )),
                unreadTotal: (() => {
                    const currentConversation = state.conversations.find((conversation) => conversation.id === conversationId);
                    const previousUnreadCount = currentConversation
                        ? normalizeUnreadCount(currentConversation.unread_messages_count)
                        : normalizeUnreadCount(state.selectedConversation?.id === conversationId
                            ? state.selectedConversation.unread_messages_count
                            : 0);

                    return Math.max(0, state.unreadTotal + (nextUnreadCount - previousUnreadCount));
                })(),
            }));

            return readState;
        } catch (error) {
            if (!silent) {
                set({
                    error: toErrorMessage(error, 'No se pudo marcar la conversación como leída.'),
                });
            }

            return null;
        }
    },

    createConversation: async (participantIds) => {
        set({ isCreatingConversation: true, error: null });

        try {
            const response = await chatService.createConversation({
                scope: 'admin_direct',
                participant_ids: participantIds,
            });
            const normalizedConversation = normalizeConversation(response.data);

            set((state) => ({
                isCreatingConversation: false,
                selectedConversation: normalizedConversation,
                conversations: [normalizedConversation, ...state.conversations.filter(
                    (conversation) => conversation.id !== normalizedConversation.id
                )],
            }));

            return normalizedConversation;
        } catch (error) {
            set({
                isCreatingConversation: false,
                error: toErrorMessage(error, 'No se pudo crear la conversación.'),
            });

            return null;
        }
    },

    findOrCreateAdminDirectConversation: async (targetUserId) => {
        const current = get();
        const existingInState = findConversationByTargetUser(current.conversations, targetUserId);

        if (existingInState) {
            set({ selectedConversation: existingInState });
            return existingInState;
        }

        try {
            const response = await chatService.getConversations({ per_page: 50 });
            const normalizedConversations = response.data.map(normalizeConversation);
            const existingInApi = findConversationByTargetUser(normalizedConversations, targetUserId);

            set((state) => ({
                conversations: normalizedConversations,
                conversationsMeta: response.meta,
                selectedConversation: existingInApi ?? state.selectedConversation,
            }));

            if (existingInApi) {
                return existingInApi;
            }
        } catch {
            // Si falla el prefetch, continuar con createConversation.
        }

        return get().createConversation([targetUserId]);
    },

    appendRealtimeMessage: (eventPayload) => {
        set((state) => {
            const conversationId = eventPayload.conversation_id;
            const incomingMessage = eventPayload.message;
            const currentUserId = useAuthStore.getState().user?.id ?? null;
            const isOwnMessage = currentUserId !== null && incomingMessage.sender_id === currentUserId;
            const isConversationActive = state.activeConversationId === conversationId;
            const previousMessages = state.messagesByConversation[conversationId] ?? {
                items: [],
                meta: null,
            };
            const hasMessageAlready = previousMessages.items.some((message) => message.id === incomingMessage.id);

            const nextMessages = upsertMessageDescending(previousMessages.items, incomingMessage);

            const currentConversation = state.conversations.find((conversation) => conversation.id === conversationId);
            const updatedConversation = currentConversation
                ? {
                    ...currentConversation,
                    last_message: incomingMessage,
                    last_message_at: incomingMessage.created_at,
                    unread_messages_count: isConversationActive
                        ? 0
                        : (
                            normalizeUnreadCount(currentConversation.unread_messages_count)
                            + (!isOwnMessage && !hasMessageAlready ? 1 : 0)
                        ),
                }
                : null;
            const shouldIncreaseUnreadTotal = !isOwnMessage && !isConversationActive && !hasMessageAlready;

            return {
                messagesByConversation: {
                    ...state.messagesByConversation,
                    [conversationId]: {
                        ...previousMessages,
                        items: nextMessages,
                    },
                },
                selectedConversation: state.selectedConversation?.id === conversationId
                    ? {
                        ...state.selectedConversation,
                        last_message: incomingMessage,
                        last_message_at: incomingMessage.created_at,
                        unread_messages_count: isConversationActive
                            ? 0
                            : (
                                normalizeUnreadCount(state.selectedConversation.unread_messages_count)
                                + (!isOwnMessage && !hasMessageAlready ? 1 : 0)
                            ),
                    }
                    : state.selectedConversation,
                conversations: updatedConversation
                    ? upsertConversationById(state.conversations, updatedConversation)
                    : state.conversations,
                unreadTotal: shouldIncreaseUnreadTotal
                    ? state.unreadTotal + 1
                    : state.unreadTotal,
            };
        });
    },

    incrementUnreadFromPush: (conversationId) => {
        set((state) => {
            if (!Number.isInteger(conversationId) || conversationId <= 0) {
                return state;
            }

            if (state.activeConversationId === conversationId) {
                return state;
            }

            const currentConversation = state.conversations.find((conversation) => conversation.id === conversationId);

            const shouldIncreaseUnreadTotal = true;

            if (!currentConversation) {
                return {
                    unreadTotal: shouldIncreaseUnreadTotal ? state.unreadTotal + 1 : state.unreadTotal,
                };
            }

            const nextUnreadCount = normalizeUnreadCount(currentConversation.unread_messages_count) + 1;
            const updatedConversation = {
                ...currentConversation,
                unread_messages_count: nextUnreadCount,
            };

            return {
                conversations: upsertConversationById(state.conversations, updatedConversation),
                selectedConversation: state.selectedConversation?.id === conversationId
                    ? {
                        ...state.selectedConversation,
                        unread_messages_count: nextUnreadCount,
                    }
                    : state.selectedConversation,
                unreadTotal: shouldIncreaseUnreadTotal ? state.unreadTotal + 1 : state.unreadTotal,
            };
        });
    },

    setActiveConversationId: (conversationId) => set({ activeConversationId: conversationId }),
    clearError: () => set({ error: null }),
    clearSelectedConversation: () => set({ selectedConversation: null }),
}));
